import VisuallyHidden from '@reach/visually-hidden';
import PropTypes from 'prop-types';
import React, { Component } from 'react';
import BalanceSheet from '../Balancesheet/BalanceSheetContainer';
import { Box } from '../CommonComponents/OPL/Box/Box';
import { Button } from '../CommonComponents/OPL/Button/Button';
import { ButtonGroup } from '../CommonComponents/OPL/ButtonGroup/ButtonGroup';
import { Select } from '../CommonComponents/OPL/Select/Select/Select';
import { Text } from '../CommonComponents/OPL/Text/Text';
import { NetWorthChart } from '../CommonComponents/OPL/charts/NetWorthChart/NetWorthChart';
import { useNetWorthChartData } from '../CommonComponents/OPL/charts/NetWorthChart/hooks/useNetWorthChartData';
import { DetailNetWorth } from './components/DetailNetWorth/DetailNetWorth';
import { NetWorthAccountsTable } from './components/NetWorthAccountsTable/NetWorthAccountsTable';

const NetWorthReportChart = ({ clientId, range }) => {
  const chartData = useNetWorthChartData(clientId, range);
  return <NetWorthChart data={chartData} />;
};

class NetWorthReportView extends Component {
  constructor(props) {
    super(props);

    this.changeHistory = this.changeHistory.bind(this);
    this.state = { activeTab: 'balanceSheet' };
  }

  componentDidMount() {
    const { fetchData, clientId, portalName } = this.props;

    document.title = `Net Worth Report | ${portalName}`;

    fetchData(null, clientId);
  }

  componentWillUnmount() {
    const { resetState } = this.props;

    resetState();
  }

  changeHistory(range) {
    const { setRange, clientId } = this.props;
    setRange(range, clientId);
  }

  toggleView(type) {
    this.setState({
      activeTab: type,
    });
  }

  render() {
    const {
      clientId,
      is_initialized: isInitialized,
      NetTotalInRed,
      CurrentNetWorth,
      AccountDetailed,
      range,
      hasAccessToLinkAccounts,
      showNetWorth,
      historyOptions,
    } = this.props;

    const { activeTab } = this.state;

    if (isInitialized) {
      const liabilitiesList = AccountDetailed.Liabilities.map(liability => ({
        ...liability,
        Items: liability.Items.map(item => ({
          ...item,
          Value: item.Value * -1,
        })),
      }));

      const masterAccountList = AccountDetailed.Assets.concat(liabilitiesList);

      if (!showNetWorth) {
        return null;
      }

      return (
        <div id="NetWorthReport">
          <Box as="header" display="flex" alignItems="center" justifyContent="space-between">
            <VisuallyHidden as="h1">Net Worth</VisuallyHidden>
            <h2>
              Net Worth{' '}
              <Text
                as="span"
                color={NetTotalInRed ? 'red.500' : 'primary.500'}
                fontSize="0.8em"
                ml="10px"
                fontWeight="normal"
              >
                {`$${ADV.util.number_format(Math.abs(CurrentNetWorth))}`}
              </Text>
            </h2>

            <Box>
              <ButtonGroup isAttached>
                <Button
                  variant={activeTab === 'balanceSheet' ? 'primary' : 'secondary'}
                  onClick={() => this.toggleView('balanceSheet')}
                >
                  Balance Sheet View
                </Button>
                <Button
                  variant={activeTab === 'detailNetWorth' ? 'primary' : 'secondary'}
                  onClick={() => this.toggleView('detailNetWorth')}
                >
                  Net Worth Details View
                </Button>
                <Button
                  variant={activeTab === 'accounts' ? 'primary' : 'secondary'}
                  onClick={() => this.toggleView('accounts')}
                >
                  Accounts View
                </Button>
              </ButtonGroup>
            </Box>
          </Box>

          {activeTab === 'detailNetWorth' && <DetailNetWorth />}

          {activeTab === 'accounts' && (
            <>
              <Box width="200px">
                <Select
                  aria-label="Time range"
                  options={historyOptions}
                  value={
                    range
                      ? historyOptions.find(({ value }) => value === range)
                      : historyOptions.find(({ isDefault }) => isDefault)
                  }
                  onChange={opt => {
                    if (opt) {
                      this.changeHistory(opt.value);
                    }
                  }}
                />
              </Box>

              <section className="chart">
                <section className="NetWorthReport_BarChart">
                  <div className="chart-container">
                    <NetWorthReportChart clientId={clientId} range={range} />
                  </div>
                </section>
              </section>
            </>
          )}

          {(activeTab === 'accounts' || activeTab === 'balanceSheet') && (
            <section className="tables">
              {activeTab === 'accounts' && (
                <NetWorthAccountsTable
                  accountGroups={masterAccountList}
                  descriptors={AccountDetailed.Descriptors}
                  clientId={clientId}
                  hasAccessToLinkAccounts={hasAccessToLinkAccounts}
                />
              )}

              {activeTab === 'balanceSheet' && <BalanceSheet clientId={clientId} />}
            </section>
          )}
        </div>
      );
    }
    return null;
  }
}

NetWorthReportView.defaultProps = {
  CurrentNetWorth: null,
  AccountDetailed: null,
  historyOptions: [],
};

NetWorthReportView.propTypes = {
  clientId: PropTypes.string.isRequired,
  is_initialized: PropTypes.bool.isRequired,
  NetTotalInRed: PropTypes.bool.isRequired,
  CurrentNetWorth: PropTypes.number,
  AccountDetailed: PropTypes.shape({
    Liabilities: PropTypes.arrayOf(PropTypes.shape({})),
    Assets: PropTypes.arrayOf(PropTypes.shape({})),
    Descriptors: PropTypes.arrayOf(PropTypes.string),
  }),
  range: PropTypes.string.isRequired,
  setRange: PropTypes.func.isRequired,
  fetchData: PropTypes.func.isRequired,
  resetState: PropTypes.func.isRequired,
  hasAccessToLinkAccounts: PropTypes.bool.isRequired,
  showNetWorth: PropTypes.bool.isRequired,
  portalName: PropTypes.string.isRequired,
  historyOptions: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.string,
      label: PropTypes.string,
      isDefault: PropTypes.bool,
    }),
  ),
};

export default NetWorthReportView;
